import { AHText, Box, useTime } from "arkh";
import React, { useLayoutEffect, useMemo, useState } from "react";

import { graphql, useLazyLoadQuery, useMutation } from "react-relay";

import { UserLoggedInNavigatorScreenProps } from "../../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { WorkspaceTodoScheduleQuery } from "./__generated__/WorkspaceTodoScheduleQuery.graphql";
import { withWebModalStack } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModalStack";
import { LazyQueryNetworkBoundrary } from "../../../../components/mobile/QueryNetworkBoundrary";
import { FormattedMessage } from "react-intl";
import { WebDateSelector } from "../../../../components/mobile/WebDateSelector/WebDateSelector";
import { useWorkspaceTimeZone } from "../../../../OS/WorkspaceTimezone";
import { DateTime } from "luxon";
import { OrgButton } from "../../OrgButton";
import { WebModalHeader } from "./WorkspaceTodoEditMain/WebModalHeader";
import { WorkspaceTodoSchedulePublishMutation } from "./__generated__/WorkspaceTodoSchedulePublishMutation.graphql";
import { useIntlTokens } from "../../../../components/mobile/useIntlTokens";
import { WorkspaceScheduleArchiveDate } from "../../WorkspaceTodoSignalShared/WorkpaceScheduleArchiveDate";
import { RadioButton } from "../../WorkspaceTodoSignalShared/RadioButton";

function getMinDate(a: DateTime | null, b: DateTime | null): DateTime | null {
  if (!a && !b) return null;
  if (!a) return b;
  if (!b) return a;

  return a < b ? a : b;
}

function InnerWorkspaceTodoSchedule(
  props: UserLoggedInNavigatorScreenProps<"WorkspaceTodoSchedule"> & {
    fetchKey: string;
  }
) {
  const data = useLazyLoadQuery<WorkspaceTodoScheduleQuery>(
    graphql`
      query WorkspaceTodoScheduleQuery($workspaceTodoId: ID!) {
        todoWorkspace(id: $workspaceTodoId) @required(action: THROW) {
          id
          updatedAtDate {
            ...FormattedDate
            isoString
          }
          receiverStores {
            id
          }
          expireAtDate {
            isoString
          }
        }
      }
    `,
    {
      workspaceTodoId: props.route.params.workspaceTodoId,
    },
    {
      fetchPolicy: "store-or-network",
      fetchKey: props.fetchKey,
    }
  );

  useLayoutEffect(() => {
    props.navigation.setOptions({
      header: () => (
        <WebModalHeader
          title={
            <>
              <WebModalHeader.Title>
                <FormattedMessage
                  defaultMessage="Publish Todo"
                  description="Workspace publish todo screen title"
                />
              </WebModalHeader.Title>
            </>
          }
          close={
            <>
              <WebModalHeader.CloseButton onPress={props.navigation.goBack} />
            </>
          }
        />
      ),
    });
  }, [props.navigation, props.route.params.workspaceId]);

  const [mode, setMode] = useState<"SCHEDULE" | "PUBLISH">("SCHEDULE");

  const now = useTime();

  const [archiveDate, setArchiveDate] = useState<DateTime | null>(null);

  const maxDate = useMemo(() => {
    const expireDate = data.todoWorkspace.expireAtDate
      ? DateTime.fromISO(data.todoWorkspace.expireAtDate.isoString)
      : null;

    return getMinDate(expireDate, archiveDate)?.minus({
      minute: 1,
    });
  }, [data.todoWorkspace.expireAtDate, archiveDate]);

  return (
    <Box style={{ flex: 1 }}>
      <Box color="background" cornerRadius="large" style={{ flex: 1 }}>
        <Box space="large" spaceBottom="large" style={{ flex: 1 }}>
          <Box
            style={{ flexDirection: "row", justifyContent: "space-between" }}
          >
            <Box space="medium" style={{ flex: 1 }}>
              <RadioButton
                selected={mode === "SCHEDULE"}
                icon="schedule"
                onPress={() => {
                  setMode("SCHEDULE");
                }}
              >
                <FormattedMessage
                  defaultMessage="Schedule"
                  description="Workspace Todo Schedule button"
                />
              </RadioButton>
            </Box>
            <Box space="medium" style={{ flex: 1 }}>
              <RadioButton
                icon="send"
                selected={mode === "PUBLISH"}
                onPress={() => {
                  setMode("PUBLISH");
                }}
              >
                <FormattedMessage
                  defaultMessage="Publish Now"
                  description="Workspace Todo Schedule button"
                />
              </RadioButton>
            </Box>
          </Box>
          <Box space="medium" style={{ flex: 1 }}>
            {mode === "PUBLISH" ? (
              <PublishNow
                numberOfStores={data.todoWorkspace.receiverStores.length}
                id={props.route.params.workspaceTodoId}
                archiveDate={archiveDate}
                setArchiveDate={setArchiveDate}
                onCompleted={() => {
                  props.navigation.popToTop();
                }}
              />
            ) : (
              <Schedule
                archiveDate={archiveDate}
                setArchiveDate={setArchiveDate}
                id={props.route.params.workspaceTodoId}
                numberOfStores={data.todoWorkspace.receiverStores.length}
                minDate={now.plus({
                  minute: 10,
                })}
                maxDate={maxDate ?? undefined}
                onCompleted={() => {
                  props.navigation.pop();
                }}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function Schedule(props: {
  id: string;
  onCompleted: () => void;
  numberOfStores: number;
  minDate: DateTime;
  maxDate?: DateTime;
  archiveDate: DateTime | null;
  setArchiveDate: (date: DateTime | null) => void;
}) {
  const [setPublishAt, isInFlight] = useMutation(graphql`
    mutation WorkspaceTodoScheduleSetPublishAtMutation(
      $id: ID!
      $publishAt: String
      $scheduledArchiveAt: String
    ) @raw_response_type {
      todoWorkspace(id: $id) {
        updateScheduledPublishAt(
          newPublishAt: $publishAt
          scheduledArchiveAt: $scheduledArchiveAt
        ) {
          id
          scheduledPublishAt {
            isoString
            ...FormattedDate
          }
          scheduledArchiveAt {
            isoString
            ...FormattedDate
          }
        }
      }
    }
  `);

  const tz = useWorkspaceTimeZone();
  const [date, setDate] = useState<DateTime | null>(null);
  const intl_tokens = useIntlTokens();
  const now = useTime();
  return (
    <Box style={{ flex: 1 }}>
      <Box style={{ flex: 1 }}>
        <AHText type="subtitle">
          <FormattedMessage
            description="WorkspaceTodoEditMain Date Title"
            defaultMessage="Publication date"
          />
        </AHText>

        <Box spaceVertical="small" style={{ flexDirection: "row", flex: 1 }}>
          <WebDateSelector
            type="datetime"
            timezone={tz}
            placeholder={
              <FormattedMessage
                description="WorkspaceTodoEditMain Date Placeholder"
                defaultMessage="add schedule"
              />
            }
            minDate={props.minDate}
            maxDate={props.maxDate}
            value={date}
            onChangeValue={(val) => {
              setDate(val);
            }}
          />
        </Box>
      </Box>
      <WorkspaceScheduleArchiveDate
        minDate={date ?? now}
        archiveDate={props.archiveDate}
        onChangeArchiveDate={props.setArchiveDate}
      />
      <AHText type="body" color="text-primary">
        <FormattedMessage
          defaultMessage="Your todo will be scheduled and published to selected units at the chosen time setting.{br}Please make sure all the details are correct before you continue."
          description="WorkspaceTodoSchedule: publish todo dialog message."
          values={{
            br: "\n",
          }}
        />
      </AHText>
      <Box
        style={{ flexDirection: "row", justifyContent: "flex-end" }}
        spaceTop="large"
      >
        <Box spaceRight="small">
          <OrgButton
            backgroundColor="neutral"
            tintColor="text-inverted"
            onPress={props.onCompleted}
            title={intl_tokens.Cancel}
          />
        </Box>
        <OrgButton
          backgroundColor="primary"
          tintColor="text-inverted"
          loading={isInFlight}
          disabled={!date}
          onPress={() => {
            setPublishAt({
              variables: {
                id: props.id,
                publishAt: date?.toISO() ?? null,
                scheduledArchiveAt: props.archiveDate?.toISO() ?? null,
              },
              onCompleted: props.onCompleted,
            });
          }}
          title={
            <FormattedMessage
              description="WorkspaceTodoSchedule: publish todo dialog button"
              values={{
                storeCount: props.numberOfStores,
              }}
              defaultMessage="Schedule todo to {storeCount, plural, one {1 unit} other {# units}}"
            />
          }
        />
      </Box>
    </Box>
  );
}

function PublishNow(props: {
  id: string;
  onCompleted: () => void;
  numberOfStores: number;
  archiveDate: DateTime | null;
  setArchiveDate: (date: DateTime | null) => void;
}) {
  const intl_tokens = useIntlTokens();
  const now = useTime();
  return (
    <Box style={{ flex: 1, justifyContent: "space-between" }}>
      <Box
        style={{
          flexDirection: "column",
          justifyContent: "flex-end",
          flex: 1,
        }}
      >
        <WorkspaceScheduleArchiveDate
          minDate={now}
          archiveDate={props.archiveDate}
          onChangeArchiveDate={props.setArchiveDate}
        />
        <AHText type="body" color="text-primary">
          <FormattedMessage
            values={{ br: "\n" }}
            defaultMessage="Your todo will be published directly to selected units.{br}Please make sure all the details are correct before you continue."
            description="WorkspaceTodoSchedule: publish now todo dialog message."
          />
        </AHText>
        <Box
          spaceTop="large"
          style={{
            flexDirection: "row",
            justifyContent: "flex-end",
          }}
        >
          <Box spaceRight="small">
            <OrgButton
              backgroundColor="neutral"
              tintColor="text-inverted"
              onPress={props.onCompleted}
              title={intl_tokens.Cancel}
            />
          </Box>
          <WorkspaceTodoEditMainPublishButton
            onCompleted={props.onCompleted}
            numberOfStores={props.numberOfStores}
            todoId={props.id}
            scheduledArchiveAt={props.archiveDate}
          />
        </Box>
      </Box>
    </Box>
  );
}

function WorkspaceTodoEditMainPublishButton(props: {
  todoId: string;
  onCompleted: () => void;
  numberOfStores: number;
  scheduledArchiveAt?: DateTime | null;
}) {
  const [publish, isPublishing] =
    useMutation<WorkspaceTodoSchedulePublishMutation>(graphql`
      mutation WorkspaceTodoSchedulePublishMutation(
        $id: ID!
        $scheduledArchiveAt: String
      ) {
        todoWorkspace(id: $id) {
          publish(scheduledArchiveAt: $scheduledArchiveAt) {
            status
          }
        }
      }
    `);

  const onPress = () => {
    publish({
      onCompleted() {
        props.onCompleted();
      },
      variables: {
        id: props.todoId,
        scheduledArchiveAt: props.scheduledArchiveAt?.toISO() ?? null,
      },
    });
  };
  return (
    <OrgButton
      backgroundColor="primary"
      tintColor="text-inverted"
      title={
        <FormattedMessage
          description="Publish todo dialog button"
          values={{
            storeCount: props.numberOfStores,
          }}
          defaultMessage="Publish to {storeCount, plural, one {1 unit} other {# units}}"
        />
      }
      onPress={onPress}
      loading={isPublishing}
    />
  );
}

function WorkspaceTodoScheduleC(
  props: UserLoggedInNavigatorScreenProps<"WorkspaceTodoSchedule">
) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => (
        <InnerWorkspaceTodoSchedule fetchKey={fetchKey} {...props} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

export const WorkspaceTodoSchedule = withWebModalStack(
  WorkspaceTodoScheduleC,
  true,
  {
    height: 500,
    width: 600,
  }
);
